import {LobbyImage} from '@colonist/types'
import {createCheckboxIn, createDivIn, createExternalImgIn, createHeaderIn, createImgIn, createInputIn, setElementInnerHTML, createSpanIn} from '../../ui_utils/DomUtils'
import {UIStoreGiftBaseSelectionController} from './UIStoreGiftBaseSelectionController'
import {getLobbyImagePath} from '../../ui_utils/Utils'
import {UIAjaxWrapper} from '../../network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../../network/UIAPIEndpoints'
import {AjaxDataTypes} from '../../../../../shared/AjaxDataTypes'
import {clientI18n} from '../../ClientI18n'
import {ViewPopup} from '../../ui_popup/ViewPopup'
import {ViewPopupOptions} from '../../ui_popup/ViewPopupOptions'
import IconPlayerLoggedIn from '@static/images/icon_player_loggedin.svg'
import IconSearch from '@static/images/icon_search.svg'
import IconSpinner from '@static/images/icon_spinner.svg'
import IconCross from '@static/images/icon_cross.svg'
import IconCheckmark from '@static/images/icon_checkmark.svg'

export class UIStoreGiftRecipientSelectionController extends UIStoreGiftBaseSelectionController {
    selectExistingPlayerToggle: HTMLInputElement
    private giftLinkToggle: HTMLInputElement
    private recipientUsernameInputField: HTMLInputElement
    private userIcon: HTMLImageElement

    private searchIcon: HTMLImageElement
    private spinnerIcon: HTMLImageElement
    private crossIcon: HTMLImageElement
    private checkMarkIcon: HTMLImageElement
    private inputFieldContainer: HTMLDivElement
    private infoContainer: HTMLDivElement
    private timer: ReturnType<typeof setTimeout>

    private _recipientUsername: string | undefined

    constructor(parentDiv: HTMLDivElement, sidebarContainer: HTMLDivElement, hideGiftOverlayAction: () => void, checkoutAction: () => void, username?: string) {
        super(parentDiv, sidebarContainer, hideGiftOverlayAction, checkoutAction)
        if(username) {
            this.setRecipient(username)
        }
    }

    get isGiftLinkSelected(): boolean {
        return this.giftLinkToggle.checked && !this.selectExistingPlayerToggle.checked
    }

    get recipientUsername(): string | undefined {
        return this._recipientUsername
    }

    setRecipient(username: string) {
        this.resetInputField()
        this.recipientUsernameInputField.value = username
        void this.queryUsernames()
    }

    protected override createSidebarButton(): void {
        super.createSidebarButton()
        setElementInnerHTML(this.selectionTitle, {key: 'strings:storePage.sections.giftOverlay.recipientSelection.buttonTitle'})
    }

    protected override createPageContent(): void {
        super.createPageContent()
        createHeaderIn(this.pageTitle, {key: 'strings:storePage.sections.giftOverlay.recipientSelection.tabTitle'}, '', '', 'H2')

        this.createOptions()
        this.mainCTAButton = this.createNextButton()
    }

    private createOptions() {
        this.createSelectExistingPlayer()
        this.createGiftLink()
        this.selectExistingPlayerToggle.click()
    }

    private createSelectExistingPlayer() {
        const selectExistingPlayerContainer = createDivIn(this.pageContainer, 'store-gift-overlay-recipient-selection-option-container')
        const toggleAndLabelContainer = createDivIn(selectExistingPlayerContainer, 'recipient-selection-toggle-and-label-container')
        this.inputFieldContainer = createDivIn(selectExistingPlayerContainer, 'recipient-selection-input-field-container')
        this.infoContainer = createDivIn(selectExistingPlayerContainer, 'recipient-selection-info-container')
        createSpanIn(this.infoContainer, 'recipient-selection-info-message', {key: 'strings:storePage.sections.giftOverlay.recipientSelection.errorMessageRecipientInputField'})
        this.infoContainer.classList.add('hidden')
        this.selectExistingPlayerToggle = createCheckboxIn(toggleAndLabelContainer, '', '', (e) => {
            e.stopPropagation()
            const isSelected = this.selectExistingPlayerToggle.checked
            this.resetInputField()
            if(isSelected) {
                this.recipientUsernameInputField.value = ''
                selectExistingPlayerContainer.classList.add('selected')
                this.inputFieldContainer.classList.remove('hidden')
                if(this.giftLinkToggle.checked) this.giftLinkToggle.click()
            } else {
                selectExistingPlayerContainer.classList.remove('selected')
                this.inputFieldContainer.classList.add('hidden')
                this.disableNextButton()
            }
        })
        selectExistingPlayerContainer.addEventListener('click', () => this.selectExistingPlayerToggle.click())
        createHeaderIn(toggleAndLabelContainer, {key: 'strings:storePage.sections.giftOverlay.recipientSelection.giftToAnExistingUser'}, '', '', 'H3')
        this.initializeRecipientInputField(this.inputFieldContainer)
    }

    private initializeRecipientInputField(inputFieldContainer: HTMLDivElement) {
        this.userIcon = createExternalImgIn(inputFieldContainer, IconPlayerLoggedIn, {key: 'strings:colonistCommon.utils.emptyString'}, 'user-icon')
        this.recipientUsernameInputField = createInputIn(inputFieldContainer, 'recipient-selection-input')
        this.searchIcon = createExternalImgIn(inputFieldContainer, IconSearch, {key: 'strings:colonistCommon.utils.emptyString'}, 'search-icon')
        this.spinnerIcon = createExternalImgIn(inputFieldContainer, IconSpinner, {key: 'strings:colonistCommon.utils.emptyString'}, 'spinner-icon')
        this.crossIcon = createExternalImgIn(inputFieldContainer, IconCross, {key: 'strings:colonistCommon.utils.emptyString'}, 'cross-icon')
        this.checkMarkIcon = createExternalImgIn(inputFieldContainer, IconCheckmark, {key: 'strings:colonistCommon.utils.emptyString'}, 'checkmark-icon')
        this.spinnerIcon.classList.add('hidden')
        this.crossIcon.classList.add('hidden')
        this.userIcon.classList.add('hidden')
        this.checkMarkIcon.classList.add('hidden')
        this.recipientUsernameInputField.placeholder = clientI18n.t('strings:storePage.sections.giftOverlay.recipientSelection.recipientPlaceholder')
        this.recipientUsernameInputField.addEventListener('click', (e) => e.stopPropagation())
        this.searchIcon.addEventListener('click', (e) => e.stopPropagation())
        this.spinnerIcon.addEventListener('click', (e) => e.stopPropagation())
        this.checkMarkIcon.addEventListener('click', (e) => e.stopPropagation())
        this.crossIcon.addEventListener('click', (e) => {
            e.stopPropagation()
            this.resetInputField()
        })
        this.recipientUsernameInputField.addEventListener('input', (e) => {
            this.disableNextButton()
            this.markAsCompleted(false)
            void this.showLoadingState()
            this.resetSearchState()
            if(this.timer != null) clearTimeout(this.timer)
            this.timer = setTimeout(() => {
                void this.queryUsernames()
            }, 500)
        })
    }

    private createGiftLink() {
        const giftLinkContainer = createDivIn(this.pageContainer, 'store-gift-overlay-recipient-selection-option-container')
        const toggleAndLabelContainer = createDivIn(giftLinkContainer, 'recipient-selection-toggle-and-label-container')
        this.giftLinkToggle = createCheckboxIn(toggleAndLabelContainer, '', '', (e) => {
            e.stopPropagation()
            const isSelected = this.giftLinkToggle.checked
            if(isSelected) {
                giftLinkContainer.classList.add('selected')
                if(this.selectExistingPlayerToggle.checked) this.selectExistingPlayerToggle.click()
                this.enableNextButton()
                this.markAsCompleted(true)
            } else {
                giftLinkContainer.classList.remove('selected')
                this.disableNextButton()
                this.markAsCompleted(false)
            }
        })
        createHeaderIn(toggleAndLabelContainer, {key: 'strings:storePage.sections.giftOverlay.recipientSelection.giftLink.title'}, '', '', 'H3')
        giftLinkContainer.addEventListener('click', () => this.giftLinkToggle.click())
        UIStoreGiftRecipientSelectionController.createInformationButton(toggleAndLabelContainer)
    }

    private static createInformationButton(container: HTMLDivElement) {
        const infoButton = createImgIn(container, LobbyImage.InfoIcon, 'info-button')
        infoButton.onclick = (e) => {
            e.stopPropagation()
            const informationButtonOptions: ViewPopupOptions = {
                showXButton: false,
                showCheckButton: true,
            }
            const popup = new ViewPopup({key: 'strings:storePage.sections.giftOverlay.recipientSelection.giftLink.information.title'}, {key: 'strings:storePage.sections.giftOverlay.recipientSelection.giftLink.information.body'},
                () => undefined, () => undefined, true, informationButtonOptions)
            popup.contentContainer.parentElement?.classList.add('store-gift-overlay-info-popup')
            popup.show()
        }
    }

    private showLoadingState() {
        this.spinnerIcon.classList.remove('hidden')
        this.searchIcon.classList.add('hidden')
        this.crossIcon.classList.add('hidden')
        this.checkMarkIcon.classList.add('hidden')
        this.userIcon.classList.add('hidden')
        this.infoContainer.classList.add('hidden')
    }

    private showReadyToSearchState() {
        this.searchIcon.classList.remove('hidden')
        this.spinnerIcon.classList.add('hidden')
        this.crossIcon.classList.add('hidden')
        this.checkMarkIcon.classList.add('hidden')
        this.userIcon.classList.add('hidden')
        this.infoContainer.classList.add('hidden')
    }

    private showErrorState() {
        this.searchIcon.classList.add('hidden')
        this.spinnerIcon.classList.add('hidden')
        this.checkMarkIcon.classList.add('hidden')
        this.crossIcon.classList.remove('hidden')
        this.infoContainer.classList.remove('hidden')
    }

    private resetSearchState() {
        this.userIcon.classList.add('hidden')
        this.recipientUsernameInputField.classList.remove('success-search')
        this.recipientUsernameInputField.classList.remove('error-search')
    }

    private async queryUsernames() {
        const inputString = this.recipientUsernameInputField.value
        if(inputString.length == 0) {
            this.spinnerIcon.classList.add('hidden')
            this.searchIcon.classList.remove('hidden')
        }
        const shouldQueryUsername = inputString.length >= 3
        if(!shouldQueryUsername) return this.onIncorrectUsernameInput()

        const response = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiUserProfile.stickyPlayerInfo(inputString), undefined, undefined, false)
        if(this.recipientUsernameInputField.value != inputString) return

        this.resetSearchState()
        if(response == undefined) {
            this.onIncorrectUsernameInput()
            return
        } else {
            const stickyPlayerInfo = response.json as AjaxDataTypes.PublicProfileDataStickyPlayerInfo
            if(!stickyPlayerInfo.isLoggedIn) return this.onIncorrectUsernameInput()
            else this.onCorrectUsernameInput()

            const playerIcon = stickyPlayerInfo.iconImageId != null ? getLobbyImagePath(stickyPlayerInfo.iconImageId) : IconPlayerLoggedIn
            this.userIcon.setAttribute('src', playerIcon)
        }
    }

    resetInputField() {
        this.recipientUsernameInputField.value = ''
        this._recipientUsername = undefined
        this.showReadyToSearchState()
        this.resetSearchState()
        this.markAsCompleted(false)
    }

    private onIncorrectUsernameInput() {
        this.recipientUsernameInputField.classList.add('error-search')
        this._recipientUsername = undefined
        this.disableNextButton()
        this.showErrorState()
        this.markAsCompleted(false)
    }

    private onCorrectUsernameInput() {
        this.recipientUsernameInputField.classList.add('success-search')
        this._recipientUsername = this.recipientUsernameInputField.value
        this.enableNextButton()
        this.searchIcon.classList.add('hidden')
        this.spinnerIcon.classList.add('hidden')
        this.checkMarkIcon.classList.remove('hidden')
        this.userIcon.classList.remove('hidden')
        this.markAsCompleted(true)
    }
}
